<template>
  <VIcon v-bind="{ ...attrs, ...a11yAttrs }">
    <slot>{{ icon }}</slot>
  </VIcon>
</template>

<script lang="ts" setup>
import { Env } from '~/types/environment'
import { useTypedRuntimeConfig } from '~/utils/runtimeConfig'

/**
 * Wraps VIcon and makes the decorative-vs-meaningful accessibility decision
 * impossible to skip: every call site must declare `decorative` or supply a
 * name, and doing neither (or both) is reported to the developer.
 *
 * Apply the decision rule in this order:
 *   1. Is the information already conveyed by adjacent visible text?
 *        -> decorative
 *   2. Is the icon the only carrier of this information?
 *        -> label
 *   3. Is it inside an interactive control with no visible text?
 *        -> the CONTROL carries the name (e.g. SBtn aria-label) and the icon
 *           stays decorative. Never name both, or it announces twice.
 *
 * @example Decorative — the adjacent text already says "Back"
 * <SIcon :icon="icons.mdiChevronLeft" decorative />
 *
 * @example Meaningful — the icon is the only carrier of the information
 * <SIcon :icon="icons.mdiStar" :label="$t('orderHistory.firstOrder.aria')" />
 *
 * @example Icon-only control — the control is named, the icon is silent
 * <SBtn icon :aria-label="$t('cart.remove.aria')" @click="remove">
 *   <SIcon :icon="icons.mdiDeleteForeverOutline" decorative />
 * </SBtn>
 */
interface Props {
  icon?: string
  decorative?: boolean
  label?: string
}

const props = withDefaults(defineProps<Props>(), {
  icon: undefined,
  decorative: false,
  label: undefined
})

defineOptions({
  inheritAttrs: false
})

const $config = useTypedRuntimeConfig()
const attrs = useAttrs()

const isInteractive = computed(() => Object.keys(attrs).some(key => key.startsWith('onClick')))

const report = (problem: string) => {
  const message = `[SIcon] ${problem}`

  if ($config.public.ENVIRONMENT === Env.Release) {
    console.warn(message)
    return
  }

  throw new Error(message)
}

interface Resolution {
  decorative: boolean
  name?: string
  problem?: string
}

const resolution = computed<Resolution>(() => {
  const hasLabel = props.label !== undefined

  if (props.decorative && hasLabel) {
    return {
      decorative: true,
      problem: 'is both `decorative` and labelled. An icon is one or the other; a decorative icon with a name announces information the UI already shows.'
    }
  }

  if (!props.decorative && !hasLabel) {
    return {
      decorative: true,
      problem: 'has no accessibility declaration. Add `decorative` if the icon repeats adjacent visible text, or `label` if the icon is the only carrier of the information.'
    }
  }

  if (props.decorative) {
    return { decorative: true }
  }

  const name = (props.label as string).trim()

  return name
    ? { decorative: false, name }
    : {
        decorative: true,
        problem: '`label` is empty. An empty accessible name is worse than none — it suppresses the browser\'s own fallback naming. If it came from $t(), the key is present but untranslated: populate it in Lokalise, or mark the icon decorative.'
      }
})

// Reported from a watcher rather than from `a11yAttrs`, because a throw raised
// while a computed is evaluated during render is swallowed by Vue's error
// handling under the <Suspense> that wraps every page and every test render —
// the developer gets a logged warning instead of a stack. `immediate` runs
// synchronously inside setup, so the first report propagates to the caller.
watch(
  () => [resolution.value.problem, isInteractive.value] as const,
  ([problem, interactive]) => {
    if (problem) {
      report(problem)
    }

    if (interactive) {
      report('has a click listener, so Vuetify gives it role="button" and tabindex="0" with no reliable accessible name. Put the handler and the name on SBtn instead, and keep the icon decorative.')
    }
  },
  { immediate: true }
)

const a11yAttrs = computed(() => {
  const { decorative, name } = resolution.value

  if (isInteractive.value) {
    // Vuetify has already put role="button" and tabindex="0" on the icon, but no
    // keydown handler — so the control it advertises cannot be activated from the
    // keyboard at all. Name it if a name is available and leave the rest alone.
    if (name) {
      return { 'aria-label': name }
    }

    // Degrade path (production only — every other environment threw above). With
    // no name there is nothing to announce, so strip the control affordances
    // rather than ship a focusable button that has neither a name nor keyboard
    // activation.
    return { 'aria-hidden': 'true', role: undefined, tabindex: undefined }
  }

  if (decorative) {
    return { 'aria-hidden': 'true' }
  }

  return {
    'aria-hidden': 'false',
    role: 'img',
    'aria-label': name
  }
})
</script>
